import { InAppState } from "../base/InAppState";

import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

import template from "./AiCostsDetailTemplate.html";
import { _lcs } from "../../i18n";
import { AiCostFormat as F } from "./AiCostFormat";

echarts.use( [ BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer ] );

/**
 * AiCostsDetail — one client's AI cost (GET /admin/ai-cost/:organisationId),
 * or, for the id "platform", the calls no client is charged for.
 *
 * This month's budget, twelve months by feature, the daily trend, the split
 * by house and model, and the latest 50 calls with tokens, duration and cost
 * — enough to see why a client got expensive.
 */
export class AiCostsDetail extends InAppState
{
    static ROUTE = '/admin/ai-costs/:organisationId';
    static ID = 'admin-ai-costs-detail';

    async enter()
    {
        this.charts = [];
        this.data = null;
        this.organisationId = this.getParam( "organisationId" );
        this.isPlatform = this.organisationId === "platform";

        this.addEventListener( this.app.view.pageContent, "click", ( e ) =>
        {
            if ( e.target.closest( '[data-action="back"]' ) )
            {
                e.preventDefault();
                this.app.router.redirect( "/admin/ai-costs" );
            }
        } );
        this.addEventListener( window, "resize", () => this.charts.forEach( ( c ) => c.resize() ) );

        this.renderState( "loading" );

        let status = 0;
        try
        {
            const { result } = await this.app.api.get( "/admin/ai-cost/" + encodeURIComponent( this.organisationId ) );
            if ( !result || +result.status !== 200 || !result.data || !Array.isArray( result.data.months ) )
            {
                throw new Error( "Unexpected /admin/ai-cost/:id response." );
            }
            this.data = result.data;
        }
        catch ( e )
        {
            status = +e?.status || 0;
            console.error( e );
        }

        if ( !this.isActive() )
        {
            return;
        }
        if ( !this.data )
        {
            this.renderState( status === 404 ? "notfound" : "error" );
            return;
        }

        // The topbar shows the client's name now that it is known.
        this.app.view.updateContentHeader();
        this.render();
    }

    async exit()
    {
        this.disposeCharts();
        await super.exit();
    }

    disposeCharts()
    {
        ( this.charts || [] ).forEach( ( c ) => c.dispose() );
        this.charts = [];
    }

    renderState( state )
    {
        this.disposeCharts();
        this.app.view.render( this.app.view.pageContent, template, { state } );
    }

    clientName()
    {
        if ( this.isPlatform ) return _lcs( "Plattform-eigene Kosten" );
        const client = this.data && this.data.client;
        if ( client && client.isDeleted ) return _lcs( "Gelöschter Gastgeber" );
        return ( client && client.name ) || _lcs( "Gastgeber ohne Namen" );
    }

    render()
    {
        const d = this.data;
        const months = d.months;
        const current = months[ months.length - 1 ];
        const previous = months[ months.length - 2 ] || null;
        const totals = d.totals;
        const succeeded = totals.requests - totals.errors;

        let budget = null;
        if ( d.budget )
        {
            const percent = d.budget.limitEur > 0 ? Math.round( d.budget.spentEur / d.budget.limitEur * 100 ) : 100;
            budget = {
                "spent" : F.money( d.budget.spentEur ),
                "limit" : F.money( d.budget.limitEur ),
                "remaining" : F.money( d.budget.remainingEur ),
                "percent" : percent,
                "isExhausted" : !!d.budget.isExhausted,
                "tone" : percent >= 100 ? "err" : ( percent >= 80 ? "warn" : "ok" )
            };
        }

        const houseTotal = d.byAccommodation.reduce( ( sum, h ) => sum + h.costEur, 0 );
        // A list of houses only says something when calls are attributed to one.
        const houses = d.byAccommodation.some( ( h ) => h.accommodationId )
            ? d.byAccommodation.map( ( h ) =>
            {
                const thisMonth = ( current.byAccommodation || [] ).find( ( x ) => x.accommodationId === h.accommodationId );
                return {
                    "name" : h.accommodationId ? ( h.accommodationName || _lcs( "Gelöschte Unterkunft" ) ) : _lcs( "Ohne Unterkunft" ),
                    "percent" : houseTotal > 0 ? Math.round( h.costEur / houseTotal * 100 ) : 0,
                    "month" : F.money( thisMonth ? thisMonth.costEur : 0 ),
                    "requests" : F.n( h.requests ),
                    "cost" : F.money( h.costEur )
                };
            } )
            : [];

        this.disposeCharts();
        this.app.view.render( this.app.view.pageContent, template, {
            "state" : "ready",
            "isPlatform" : this.isPlatform,
            "isDeleted" : !!( d.client && d.client.isDeleted ),
            "head" : {
                "name" : this.clientName(),
                "email" : d.client ? d.client.email : null,
                "houses" : d.client ? d.client.accommodations.map( ( a ) => ( {
                    "name" : a.name || a.slug, "published" : a.status === "published"
                } ) ) : []
            },
            "budget" : budget,
            "kpi" : {
                "month" : F.money( current.costEur ),
                "forecast" : _lcs( "Prognose {amount}", { amount : F.money( F.forecast( current.costEur ) ) } ),
                "previous" : previous ? F.money( previous.costEur ) : "—",
                "previousSub" : previous ? _lcs( "{count} Anfragen", { count : previous.requests, n : F.n( previous.requests ) } ) : "",
                "total" : F.money( totals.costEur ),
                "totalSub" : _lcs( "{count} Anfragen", { count : totals.requests, n : F.n( totals.requests ) } ),
                "perRequest" : succeeded > 0 ? F.money( totals.costEur / succeeded, true ) : "—",
                "errors" : _lcs( "{n} fehlgeschlagen", { n : F.n( totals.errors ) } ),
                "errorClass" : totals.errors > 0 ? "kpi__delta--warn" : "kpi__delta--flat"
            },
            "houses" : houses,
            "features" : d.byFeature.map( ( f, i ) => ( {
                "label" : F.feature( f.feature ),
                "color" : F.featureColor( f.feature, i ),
                "requests" : _lcs( "{count} Anfragen", { count : f.requests, n : F.n( f.requests ) } ),
                "cost" : F.money( f.costEur )
            } ) ),
            "models" : d.byModel.map( ( m ) => ( {
                "model" : m.model, "requests" : F.n( m.requests ), "cost" : F.money( m.costEur )
            } ) ),
            "tokenNote" : _lcs( "{input} Eingabe- und {output} Ausgabe-Tokens", {
                input : F.compact( totals.inputTokens ), output : F.compact( totals.outputTokens ) } ),
            "calls" : d.recentCalls.map( ( c ) => ( {
                "time" : F.dateTime( c.createdAt ),
                "feature" : F.feature( c.feature ),
                "color" : F.featureColor( c.feature ),
                "user" : c.userEmail,
                "house" : c.accommodationId ? ( c.accommodationName || _lcs( "Gelöschte Unterkunft" ) ) : "—",
                "model" : c.model,
                "tokens" : F.n( c.inputTokens ) + " / " + F.n( c.outputTokens ),
                "duration" : F.duration( c.durationMs ),
                "cost" : c.status === "error" ? "—" : F.money( c.costEur, true ),
                "isError" : c.status === "error"
            } ) )
        } );

        this.drawCharts();
    }

    drawCharts()
    {
        const root = this.app.view.pageContent;
        const draw = ( id, option ) =>
        {
            const el = root.querySelector( '[data-id="' + id + '"]' );
            if ( !el ) return;
            const chart = echarts.init( el );
            chart.setOption( option );
            this.charts.push( chart );
        };

        const d = this.data;
        draw( "chart-months", F.monthlyStackOption( d.months.map( ( m ) => m.month ), d.byFeature.map( ( f, i ) => ( {
            name : F.feature( f.feature ),
            color : F.featureColor( f.feature, i ),
            values : d.months.map( ( m ) =>
            {
                const hit = m.byFeature.find( ( x ) => x.feature === f.feature );
                return hit ? hit.costEur : 0;
            } )
        } ) ) ) );
        draw( "chart-days", F.dailyOption( d.days ) );

        // The list under the donut is its legend.
        const donut = F.donutOption(
            d.byFeature.map( ( f, i ) => ( { name : F.feature( f.feature ), value : f.costEur, color : F.featureColor( f.feature, i ) } ) ),
            _lcs( "12 Monate" ) );
        donut.legend = { show : false };
        donut.series[ 0 ].center = [ "50%", "50%" ];
        draw( "chart-features", donut );
    }

    getContentHeaderData()
    {
        return {
            title : this.data ? this.clientName() : _lcs( "KI-Kosten" ),
            subtitle : _lcs( "KI-Kosten im Detail" )
        };
    }
}
